import { useCallback, useEffect, useRef, useState } from 'react';
import Flow from '@flowjs/flow.js';
import type { FieldProps } from './TextField';
import type { UploadedFile } from '../../types';
import { useFormField } from '../../hooks/useFormField';
import { useFormContext } from '../../context/FormContext';
import { QuestionWrapper } from '../QuestionWrapper';
import { formUrl } from '../../services/formUrls';

function formatFileSize(sizeBytes: number): string {
  let sizeMB = sizeBytes / 1000000;
  sizeMB = Math.round(sizeMB * 100) / 100;
  return sizeMB + ' MB';
}

function getFileExtension(fileName: string): string {
  if (!fileName) {
    return '';
  }
  return fileName.split('.').pop() || '';
}

export function FileUploadField({ fieldSchema, group }: FieldProps) {
  const field = useFormField(fieldSchema, group);
  const { settings, guidService } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const flowRef = useRef<Flow | null>(null);

  const maxFiles = fieldSchema.maxFiles ?? 10;
  const maxFileSize = fieldSchema.maxFileSize ?? 10 * 1000 * 1000;
  const maxCombinedFileSize = fieldSchema.maxCombinedFileSize ?? 14.75 * 1000 * 1000;
  const allowedFileTypes = fieldSchema.allowedFileTypes ?? ['pdf', 'jpg', 'png', 'tif'];

  const [files, setFiles] = useState<UploadedFile[]>(
    Array.isArray(field.answer) ? (field.answer as UploadedFile[]) : []
  );
  const [uploading, setUploading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const updateAnswer = useCallback(
    (nextFiles: UploadedFile[]) => {
      setFiles(nextFiles);
      field.setAnswer(nextFiles.length > 0 ? nextFiles : null);
    },
    [field]
  );

  useEffect(() => {
    if (!fileInputRef.current) {
      return;
    }

    const flow = new Flow({
      target: formUrl(settings.apiBaseUrl, 'upload'),
      chunkSize: 2 * 1024 * 1024,
      simultaneousUploads: 1,
      generateUniqueIdentifier: () => guidService.generate(),
      headers: settings.csrfToken ? { 'X-Csrf-Token': settings.csrfToken } : {},
      singleFile: true,
    });

    flow.assignBrowse(fileInputRef.current, false);

    flow.on('fileAdded', (file) => {
      const extension = getFileExtension(file.name);
      const hasExtension = allowedFileTypes.includes(extension);

      if (!hasExtension) {
        setErrorMessages(['File type is not allowed.']);
        setUploading(false);
        return false;
      }

      return true;
    });

    flow.on('filesSubmitted', () => {
      if (files.length >= maxFiles) {
        setErrorMessages([`<p>You have reached the upload limit of ${maxFiles} files.</p>`]);
        return;
      }

      const currentFile = flow.files[flow.files.length - 1];
      const currentFileSize = currentFile?.size ?? 0;
      const currentSize = files.reduce((sum, f) => sum + (f.fileSize ?? 0), 0);

      if (currentFileSize > maxFileSize) {
        setErrorMessages([msgMaxPerFile(maxFileSize)]);
        flow.cancel();
        return;
      }

      if (currentSize + currentFileSize > maxCombinedFileSize) {
        setErrorMessages([msgMaxCombined(maxCombinedFileSize)]);
        return;
      }

      setErrorMessages([]);
      setUploading(true);
      flow.upload();
    });

    flow.on('fileSuccess', (file, message) => {
      setUploading(false);
      setErrorMessages([]);

      const uploaded: UploadedFile = {
        identifier: file.uniqueIdentifier,
        originalFileName: file.name,
        savedFileName: file.uniqueIdentifier + '.' + getFileExtension(file.name),
        fileSize: file.size,
        formattedFileSize: formatFileSize(file.size),
      };

      updateAnswer([...files, uploaded]);
    });

    flow.on('fileError', (_file, message) => {
      setUploading(false);

      try {
        const obj = JSON.parse(message);
        if (obj?.Data?.errorMessages) {
          setErrorMessages(obj.Data.errorMessages);
        }
      } catch {
        setErrorMessages(['Upload failed.']);
      }
    });

    flowRef.current = flow;

    return () => {
      flow.cancel();
    };
  }, [
    settings,
    guidService,
    files,
    maxFiles,
    maxFileSize,
    maxCombinedFileSize,
    allowedFileTypes,
    updateAnswer,
  ]);

  const removeUpload = (index: number) => {
    const next = [...files];
    next.splice(index, 1);
    updateAnswer(next);
  };

  return (
    <QuestionWrapper {...field} fieldSchema={fieldSchema} useLegend>
      {errorMessages.length > 0 && (
        <div>
          {errorMessages.map((errorMessage, key) => (
            <div className="validation-msg upload-file-msg" key={key}>
              <div dangerouslySetInnerHTML={{ __html: errorMessage }} />
            </div>
          ))}
        </div>
      )}

      <div className="answer-group">
        <div className="answer">
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} />
          <button
            type="button"
            className="button continue-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload a File
          </button>

          {uploading && <p className="mt-4">Uploading...</p>}

          <div className="file-upload-area mt-4">
            {files.length === 0 && <p>Your uploaded files will appear here</p>}

            {files.length > 0 && (
              <ul>
                {files.map((file, key) =>
                  !file.removed ? (
                    <li key={key}>
                      {file.originalFileName} ({file.formattedFileSize}){' '}
                      <a href="" onClick={(e) => { e.preventDefault(); removeUpload(key); }}>
                        remove
                      </a>
                    </li>
                  ) : null
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </QuestionWrapper>
  );
}

function msgMaxPerFile(maxFileSize: number): string {
  const mb = maxFileSize / 1000000;
  return `<p>The file you have confirmed to upload is exceeding the maximum allowable file size (${mb} Mb). You can either</p><ul><li>scan and upload the document as multiple files of smaller sizes (e.g. ${mb} Mb each)</li><li>Try to reduce the file size before uploading – by reducing the resolution quality (dpi) of the file or image.</li><li>opt to send the document separately to WorkSafe via email or post. (Note there is a 15 Mb maximum email size).</li></ul>`;
}

function msgMaxCombined(maxCombinedFileSize: number): string {
  const mb = maxCombinedFileSize / 1000000;
  return `<p>The file you have confirmed to upload is exceeding the maximum combined allowable file size of all uploaded files (${mb} Mb). You can either</p><ul><li>Try to reduce the file size before uploading - by reducing the resolution quality (dpi) of the file or image.</li><li>opt to send the document separately to WorkSafe via email or post. (Note there is a 15 Mb maximum email size)</li></ul>`;
}
